import { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

/**
 * Real interactive Google Maps live-tracking map for the customer.
 * Renders the booking/service destination marker, the provider's live GPS
 * marker and the road-route polyline supplied by the backend (Google Routes).
 *
 * If the Maps JS API key is missing it renders a clear fallback instead of
 * crashing, keeping distance/ETA/tracking UI (rendered by the parent) intact.
 */

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const DESTINATION_ICON_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="#1d4ed8" stroke="#ffffff" stroke-width="1.5">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
    <circle cx="12" cy="9" r="2.6" fill="#ffffff" stroke="none"/>
  </svg>`
);

const PROVIDER_ICON_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="#059669" stroke="#ffffff" stroke-width="1.5">
    <circle cx="12" cy="12" r="9"/>
    <circle cx="12" cy="12" r="3.4" fill="#ffffff" stroke="none"/>
  </svg>`
);

export default function ProviderTrackingMap({ tracking }) {
  const mapDivRef = useRef(null);
  const loaderRef = useRef(null);
  const googleRef = useRef(null);
  const mapRef = useRef(null);
  const providerMarkerRef = useRef(null);
  const destMarkerRef = useRef(null);
  const polylineRef = useRef(null);
  const constructedDivRef = useRef(null);
  const fittedRef = useRef(false);
  const userInteractedRef = useRef(false);
  const selfFittingRef = useRef(false);

  const [mapReady, setMapReady] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const provider = tracking?.provider_location;
  const dest = tracking?.destination;
  const routePolyline = tracking?.route_polyline;

  const haveProvider = !!(provider && provider.latitude != null && provider.longitude != null);
  const haveDest = !!(dest && dest.latitude != null && dest.longitude != null);
  const keyMissing = !API_KEY;

  useEffect(() => {
    if (keyMissing) {
      setLoadError({ missingKey: true });
      return undefined;
    }
    if (!haveDest) {
      setMapReady(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        if (!loaderRef.current) {
          loaderRef.current = new Loader({
            apiKey: API_KEY,
            version: 'weekly',
            libraries: ['geometry'],
          });
        }
        const google = await loaderRef.current.load();
        if (cancelled) return;
        googleRef.current = google;
        if (!cancelled) setMapReady(true);
      } catch (e) {
        if (!cancelled) setLoadError({ message: e?.message || 'Could not load map' });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyMissing, haveDest]);

  // Construct the Map only AFTER the canvas div is mounted (mapReady=true is
  // what renders it). Previously the constructor ran while the fallback was
  // still displayed, so mapDivRef.current was null, the constructor threw and
  // the component was stuck on "Map preview unavailable" forever.
  useEffect(() => {
    const google = googleRef.current;
    if (!mapReady || !haveDest || !google) return;
    if (!mapDivRef.current) return;
    if (mapRef.current && constructedDivRef.current === mapDivRef.current) return;

    if (mapRef.current) {
      // Canvas div remounted (fallback <-> canvas switch): the old map is bound
      // to a detached node. Drop map-bound layers so they rebuild below.
      mapRef.current = null;
      providerMarkerRef.current = null;
      destMarkerRef.current = null;
      polylineRef.current = null;
    }

    const map = new google.maps.Map(mapDivRef.current, {
      center: { lat: dest.latitude, lng: dest.longitude },
      zoom: 14,
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
      gestureHandling: 'cooperative',
    });
    mapRef.current = map;
    constructedDivRef.current = mapDivRef.current;
    google.maps.event.addListener(map, 'dragstart', () => {
      userInteractedRef.current = true;
    });
    google.maps.event.addListener(map, 'zoom_changed', () => {
      // Ignore zoom changes we trigger ourselves via fitBounds.
      if (!selfFittingRef.current) userInteractedRef.current = true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, haveDest]);
// Update markers + polyline whenever new tracking data arrives.
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const google = googleRef.current;
    const map = mapRef.current;
    if (!google) return;

    const updateDestLayer = () => {
      if (haveDest) {
        const lat = dest.latitude;
        const lng = dest.longitude;
        if (!destMarkerRef.current) {
          destMarkerRef.current = new google.maps.Marker({
            map,
            position: { lat, lng },
            title: 'Service location',
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${DESTINATION_ICON_SVG}`,
              scaledSize: new google.maps.Size(32, 32),
            },
          });
        } else {
          destMarkerRef.current.setPosition({ lat, lng });
        }
      } else if (destMarkerRef.current) {
        destMarkerRef.current.setMap(null);
        destMarkerRef.current = null;
      }
    };

    const updateProviderLayer = () => {
      if (haveProvider) {
        const lat = provider.latitude;
        const lng = provider.longitude;
        if (!providerMarkerRef.current) {
          providerMarkerRef.current = new google.maps.Marker({
            map,
            position: { lat, lng },
            title: 'Your provider',
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${PROVIDER_ICON_SVG}`,
              scaledSize: new google.maps.Size(32, 32),
            },
          });
        } else {
          providerMarkerRef.current.setPosition({ lat, lng });
        }
      } else if (providerMarkerRef.current) {
        providerMarkerRef.current.setMap(null);
        providerMarkerRef.current = null;
      }
    };

    const updateRouteLayer = () => {
      if (routePolyline && google.maps.geometry?.encoding) {
        let path;
        try {
          path = google.maps.geometry.encoding.decodePath(routePolyline);
        } catch (e) {
          path = null;
        }
        if (path && path.length) {
          if (!polylineRef.current) {
            polylineRef.current = new google.maps.Polyline({
              map,
              path,
              strokeColor: '#2563eb',
              strokeOpacity: 0.9,
              strokeWeight: 4,
            });
          } else {
            polylineRef.current.setPath(path);
            polylineRef.current.setMap(map);
          }
        }
      } else if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };

    updateDestLayer();
    updateProviderLayer();
    updateRouteLayer();

    // Auto-fit only while the user hasn't taken control, and avoid re-fitting
    // on every poll: fit once on first render, then keep the provider visible
    // by refitting only when it leaves the current viewport.
    if (haveProvider && haveDest && !userInteractedRef.current) {
      const bounds = map.getBounds ? map.getBounds() : null;
      const providerPos = { lat: provider.latitude, lng: provider.longitude };
      const needFit = !bounds || !bounds.contains(providerPos);
      if (!fittedRef.current || needFit) {
        selfFittingRef.current = true;
        const b = new google.maps.LatLngBounds();
        b.extend(providerPos);
        b.extend({ lat: dest.latitude, lng: dest.longitude });
        map.fitBounds(b, 64);
        fittedRef.current = true;
        selfFittingRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, tracking]);

  const fallbackMessage = loadError?.missingKey
    ? 'Live map unavailable'
    : loadError?.message
      ? 'Map could not be loaded'
      : 'Map preview unavailable';

  return (
    <div className="h-64 rounded-xl overflow-hidden" data-testid="tracking-map">
      {mapReady && haveDest ? (
        <div ref={mapDivRef} className="h-full w-full" data-testid="tracking-map-canvas" />
      ) : (
        <div
          data-testid="tracking-map-unavailable"
          className="h-full flex items-center justify-center text-sm text-slate-500 bg-slate-100"
        >
          <div className="px-4 text-center">
            <div>{fallbackMessage}</div>
            {loadError?.missingKey && (
              <div className="mt-1 text-xs text-slate-400">
                Set REACT_APP_GOOGLE_MAPS_API_KEY to enable the live map.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}